import React, { useCallback, useEffect, useState } from "react";
import { Card, Row, Col, Spin, Modal, Table, Button, message } from "antd";
import {
  IoCheckmarkDoneCircle,
  IoToday,
  IoPeopleOutline,
} from "react-icons/io5";
import { ImDownload2 } from "react-icons/im";
import "./AdminView.css";
import { IoTime } from "react-icons/io5";
import { FaBorderAll } from "react-icons/fa";
import dayjs from "dayjs";
import { staticApis, roomBookingApi } from "../../api";
import ReleaseRoomPopup from "./ReleaseRoomPopup";
import { employeeId } from "../../userRole";
import { MONTHLY_ADMIN_RELEASE_LIMIT } from "../../utils/Constants";

const AdminView = () => {
  const [dashBoardData, setDashBoardData] = useState({
    activeRooms: 0,
    totalRooms: 0,
    todaysMeetings: [],
    ongoingMeetings: [],
    totalUsers: 0,
    roomsUnderMaintanence: 0,
  });

  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [isOngoingModalVisible, setIsOngoingModalVisible] = useState(false);
  const [isTodayModalVisible, setIsTodayModalVisible] = useState(false);
  const [releaseModal, setReleaseModal] = useState(null);
  const [releaseQuota, setReleaseQuota] = useState({
    used: 0,
    limit: MONTHLY_ADMIN_RELEASE_LIMIT,
    remaining: MONTHLY_ADMIN_RELEASE_LIMIT,
  });

  const loadDashboard = useCallback(async () => {
    const todayDate = dayjs().format("YYYY-MM-DD");
    const currentTime = dayjs().format("HH:mm:ss");
    const params = {
      date: todayDate,
      time: currentTime,
    };
    setLoading(true);
    try {
      const resp = await staticApis.getAdminDashboardData(params);
      setDashBoardData(resp?.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
    setLoading(false);
  }, []);

  const loadReleaseQuota = useCallback(async () => {
    if (!employeeId) return;
    try {
      const res = await roomBookingApi.getAdminReleaseQuota(employeeId);
      if (res?.data) setReleaseQuota(res.data);
    } catch (err) {
      console.error("Error fetching admin release quota:", err);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadReleaseQuota();
  }, [loadDashboard, loadReleaseQuota]);

  const showOngoingModal = () => {
    setIsOngoingModalVisible(true);
  };

  const showTodayModal = () => {
    setIsTodayModalVisible(true);
  };

  const handleOngoingOk = () => {
    setIsOngoingModalVisible(false);
  };

  const handleTodayOk = () => {
    setIsTodayModalVisible(false);
  };

  const handleCancel = () => {
    setIsOngoingModalVisible(false);
    setIsTodayModalVisible(false);
  };

  const openRelease = (meeting) => {
    if (!meeting?.rtId) {
      message.error("Cannot release this booking — missing slot id.");
      return;
    }
    setReleaseModal({
      rtId: meeting.rtId,
      rbId: meeting.rbId,
      originalBooker: meeting.createdByEmpCode,
      originalBookerName: meeting.createdBy,
    });
  };

  const handleReleaseDone = () => {
    setReleaseModal(null);
    setIsOngoingModalVisible(false);
    setIsTodayModalVisible(false);
    loadDashboard();
    loadReleaseQuota();
  };

  const downloadExceptionsReport = async () => {
    setDownloadingReport(true);
    try {
      const month = dayjs().format("YYYY-MM");
      const blob = await roomBookingApi.downloadExceptionsReport({ month });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `booking-exceptions-and-releases-${month}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Exceptions and releases report downloaded");
    } catch (err) {
      console.error("Error downloading exception report:", err);
      message.error("Failed to download exception report");
    }
    setDownloadingReport(false);
  };

  const meetingColumns = [
    {
      title: "Room Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Meeting Time",
      dataIndex: "timings",
      key: "timings",
      sorter: (a, b) =>
        new Date(`1970-01-01T${a.startTime}`) -
        new Date(`1970-01-01T${b.startTime}`),
    },
    {
      title: "Meeting Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        record.rtId ? (
          <Button
            danger
            size="small"
            disabled={releaseQuota.remaining < 1}
            onClick={() => openRelease(record)}
          >
            Release room
          </Button>
        ) : null,
    },
  ];

  const mapMeetingRows = (meetings) =>
    (meetings || [])
      .filter((meeting) => meeting?.name && meeting?.rtId)
      .map((meeting, index) => {
        const startTime = new Date(
          `1970-01-01T${meeting.startTime}`
        ).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        const endTime = new Date(
          `1970-01-01T${meeting.endTime}`
        ).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return {
          key: meeting.rtId || index,
          name: meeting.name,
          timings: `${startTime} - ${endTime}`,
          subject: meeting.subject,
          createdBy: meeting.createdBy,
          createdByEmpCode: meeting.createdByEmpCode,
          startTime: meeting.startTime,
          rtId: meeting.rtId,
          rbId: meeting.rbId,
        };
      });

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <span style={{ marginLeft: "10px" }}>Fetching...</span>
      </div>
    );
  }

  return (
    <div className="booking-status-container">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<ImDownload2 />}
          loading={downloadingReport}
          onClick={downloadExceptionsReport}
        >
          Download exceptions & releases report
        </Button>
      </div>
      <Row gutter={[10, 32]} justify="space-between">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card bordered className="status-box half-box">
            <IoCheckmarkDoneCircle className="icon" />
            <span className="count">{dashBoardData.activeRooms}</span>
            <div>Active Rooms</div>
          </Card>
          <Card bordered className="status-box half-box">
            <ImDownload2 className="icon" />
            <span className="count">{dashBoardData.roomsUnderMaintanence}</span>
            <div>Rooms Under Maintenance</div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={12}>
          <Row gutter={[10, 32]}>
            <Col span={12}>
              <Card bordered className="status-box full-box">
                <div style={{ alignItems: "center", margin: "auto" }}>
                  <FaBorderAll className="icon-mid" />
                  <div className="count-mid">{dashBoardData.totalRooms}</div>
                  <div>Total Conference Rooms</div>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card bordered className="status-box full-box">
                <div style={{ alignItems: "center", margin: "auto" }}>
                  <IoPeopleOutline className="icon-mid" />
                  <div className="count-mid">{dashBoardData.totalUsers}</div>
                  <div>Number Of Users</div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            bordered
            className="status-box half-box"
            onClick={
              dashBoardData.ongoingMeetings &&
              dashBoardData.ongoingMeetings.length > 0
                ? showOngoingModal
                : null
            }
          >
            <IoTime className="icon" />
            <span className="count">
              {dashBoardData.ongoingMeetings.length}
            </span>
            <div>Ongoing Bookings</div>
          </Card>
          <Card
            bordered
            className="status-box half-box"
            onClick={
              dashBoardData.todaysMeetings &&
              dashBoardData.todaysMeetings.length > 0
                ? showTodayModal
                : null
            }
          >
            <IoToday className="icon" />
            <span className="count">{dashBoardData.todaysMeetings.length}</span>
            <div>Today&apos;s Bookings</div>
          </Card>
        </Col>
      </Row>
      <Modal
        title="Ongoing Booking Details"
        open={isOngoingModalVisible}
        onOk={handleOngoingOk}
        width={"800px"}
        onCancel={handleCancel}
        footer={null}
        styles={{ body: { padding: 0 } }}
        style={{ top: 60 }}
      >
        <Table
          columns={meetingColumns}
          dataSource={mapMeetingRows(dashBoardData.ongoingMeetings)}
          pagination={false}
          scroll={{ y: 150 }}
        />
      </Modal>
      <Modal
        title="Today's Booking Details"
        open={isTodayModalVisible}
        onOk={handleTodayOk}
        width={"800px"}
        onCancel={handleCancel}
        footer={null}
        styles={{ body: { padding: 0 } }}
        style={{ top: 60 }}
      >
        <Table
          columns={meetingColumns}
          dataSource={mapMeetingRows(dashBoardData.todaysMeetings)}
          pagination={false}
          scroll={{ y: 150 }}
        />
      </Modal>
      <ReleaseRoomPopup
        open={!!releaseModal}
        modalData={releaseModal}
        onCancel={() => setReleaseModal(null)}
        onSuccess={handleReleaseDone}
      />
    </div>
  );
};

export default AdminView;
